import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';

import { triggerCacheIfNeeded } from 'helpers/menuManagement';

import { useMenuManagement } from 'hooks/useMenuManagement';

/**
 * Exports a function to trigger the menu cache and awaits the pusher notification
 *
 * Flow is...
 *  Starts as loading=true ready=false
 *  When pusher channel is setup, loading=true, ready=true
 *  Once the event comes in, it sets loading=false if applicable
 */
export function useMenuManagementMenuCache({ menuID, merchantID, companyID }) {
  const [cacheConfig, setCacheConfig] = useState({
    merchantId: null,
    companyId: null,
    menuId: menuID,
  });
  const { singleMenu } = useMenuManagement();
  const [isLoading, setIsLoading] = useState(true);

  // True once cacheConfig is populated, meaning it's safe to trigger the cache
  const isReady = cacheConfig.companyId !== null && cacheConfig.merchantId !== null;

  useEffect(() => {
    if (!singleMenu.data) return;
    if (isReady) return;

    setCacheConfig({
      companyId: parseInt(companyID, 10),
      merchantId: parseInt(merchantID, 10),
      menuId: menuID,
    });

    setIsLoading(false);
  }, [singleMenu.data, menuID, isReady, merchantID, companyID]);

  /**
   *  The using component should wait for
   *  isReady === true before triggering. This ensures that menu is in
   *  state and that we have a channel listening to the event
   */
  const triggerCache = async () => {
    try {
      await triggerCacheIfNeeded({
        menuId: cacheConfig.menuId,
        companyId: cacheConfig.companyId,
        merchantId: cacheConfig.merchantId,
      });
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  return {
    isLoading,
    isReady,
    triggerCache,
    config: {
      ...cacheConfig,
    },
  };
}

useMenuManagementMenuCache.propTypes = {
  menuId: PropTypes.string.isRequired,
  onCacheSuccess: PropTypes.func,
  onCacheError: PropTypes.func,
  showBuildMessage: PropTypes.bool,
  showPublishMessages: PropTypes.bool,
};
